import { useState, useCallback, useMemo } from 'react';
import { useApolloClient, useLazyQuery, useMutation, useQuery } from '@apollo/client/react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import { AlertTriangle, ChevronDown, CreditCard, Loader2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  cn,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
} from '@scio3/ui';
import {
  GET_SUBSCRIPTION_INFO_STRIPE,
  GET_STRIPE_CUSTOMER_TAXABLE,
  GET_UPCOMING_SUBSCRIPTION,
  GET_UPCOMING_AND_UNPAID_SUBSCRIPTION,
  STRIPE_BUILD_ELEMENT,
  UPDATE_PROMO_CODE_SUBSCRIPTION,
  UPDATE_STRIPE_EMAIL,
  REGISTER_PAYMENT_METHOD,
  GET_STRIPE_CUSTOMER_INFO,
  GET_COMPANY_INFO,
} from '@scio3/graphql-client';
import { toast } from 'sonner';
import { isEnterKey } from '@/lib/keyboard';
import { usePermissions } from '@/hooks/usePermissions';
import { useCompanyFeatures } from '@/hooks/useCompanyFeatures';
import { useAuthStore } from '@/stores/auth-store';
import { useBillingData } from '../hooks/useBillingData';
import { useBillingDialogStore } from '../stores/billing-dialog-store';
import { StripeCardForm } from '../components/StripeCardForm';
import { BillingAddressForm } from '../components/BillingAddressForm';
import { TaxIdSection } from '../components/TaxIdSection';
import { UnpaidInvoicesSection } from '../components/UnpaidInvoicesSection';
import { PromoCodeInput } from '../components/PromoCodeInput';
import { PaymentThankYouDialog } from '../dialogs/PaymentThankYouDialog';
import { AutoChargeNoticeDialog } from '../dialogs/AutoChargeNoticeDialog';
import { formatPaymentMethodLabel } from '../utils/format-payment-method';
import { COUNTRIES } from '@/features/settings/utils/profile-constants';

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/** Collapsible section matching scio1 card style + scio3 settings page pattern */
function CollapsibleSection({
  title,
  open,
  onOpenChange,
  children,
  'data-testid': testId,
}: {
  title: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
  'data-testid'?: string;
}) {
  return (
    <Collapsible open={open} onOpenChange={onOpenChange}>
      <section className="rounded-lg border border-border bg-card" data-testid={testId}>
        <CollapsibleTrigger asChild>
          <div
            className="flex items-center justify-between px-8 py-6 cursor-pointer"
            data-testid={testId ? `${testId}-trigger` : undefined}
          >
            <h2 className="text-base font-semibold">{title}</h2>
            <ChevronDown
              className={cn(
                'h-4 w-4 text-muted-foreground transition-transform',
                open && 'rotate-180',
              )}
            />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="px-8 pb-8">{children}</div>
        </CollapsibleContent>
      </section>
    </Collapsible>
  );
}

type PaymentMethodPageProps = {
  /** When embedded in sub-account edit view, pass subAccountId directly instead of URL params */
  subAccountId?: string;
  /** When embedded in team billing edit view, pass teamId directly */
  teamId?: string;
  /** Force limited view (billing info + unpaid invoices only) without requiring ?action=updatePaymentMethod URL.
   *  Used by the paywall BillingInformationDialog so scio1-style redirect URL is not needed. */
  forceLimitedView?: boolean;
  /** Called after a payment method is successfully saved. Used by the paywall dialog to refetch ME_QUERY
   *  so `isAccountInactive` flips to false and the paywall lifts. */
  onPaymentSaved?: () => void;
};

export default function PaymentMethodPage({
  subAccountId: subAccountIdProp,
  teamId: teamIdProp,
  forceLimitedView = false,
  onPaymentSaved,
}: PaymentMethodPageProps = {}) {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const subAccountId =
    subAccountIdProp ?? searchParams.get('s') ?? searchParams.get('subAccountId') ?? undefined;
  const teamId = teamIdProp ?? undefined;
  const { isRootAdmin } = usePermissions();
  const companyLoaded = useAuthStore((s) => s.companyLoaded);
  const action = searchParams.get('action');
  const hasFParam = searchParams.has('f');

  // Limited view: only show billing info + update card (used for ?action=updatePaymentMethod redirect
  // and the paywall BillingInformationDialog).
  // Sub-accounts show all sections (Billing Email, Info, Address, Discount) matching scio1
  const isLimitedView = forceLimitedView || action === 'updatePaymentMethod';

  // All hooks must be called before any early returns (React rules of hooks)
  const {
    billingEmail,
    defaultEmail,
    existingCard,
    currency: accountCurrency,
    loadingSubAccount,
    isTrial,
  } = useBillingData({ subAccountId, teamId });

  const [isUpdatingCard, setIsUpdatingCard] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentMethodOrder, setPaymentMethodOrder] = useState<string[]>([]);
  const [showThankYou, setShowThankYou] = useState(false);
  // #1076 scio1-parity: auto-charge notice popup shown after a card update when
  // the account had outstanding invoices (the `res.invoices.length` branch).
  const [showAutoChargeNotice, setShowAutoChargeNotice] = useState(false);

  // Collapsible state — scio1: email + billing info open, rest closed
  const [emailOpen, setEmailOpen] = useState(true);
  const [billingInfoOpen, setBillingInfoOpen] = useState(true);
  const [addressOpen, setAddressOpen] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [discountOpen, setDiscountOpen] = useState(false);

  // Billing email edit
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailValue, setEmailValue] = useState(billingEmail ?? defaultEmail);
  // QC #1296 — refetch billing data after save so the displayed email matches
  // the freshly-saved value without requiring a manual page refresh.
  // awaitRefetchQueries delays the toast + form-exit until the cache is fresh.
  //
  // Refetch ONLY the query active in the current context. Refetching
  // GetSubAccountInfo on a parent / team-billing account that doesn't have
  // `enableSubAccount` triggers a 403 FEATURE_NOT_ENABLED:enableSubAccount
  // from the backend RoleFeatureGuard (see useBillingData.ts:40-51 — those
  // queries are conditionally skipped on their fetch side too).
  const billingDataRefetchQuery = subAccountId
    ? 'GetSubAccountInfo'
    : teamId
      ? 'GetTeamBillingInfo'
      : 'GetCompanyInfo';
  const [updateStripeEmail, { loading: emailSaving }] = useMutation(UPDATE_STRIPE_EMAIL, {
    refetchQueries: [billingDataRefetchQuery],
    awaitRefetchQueries: true,
  });

  const { data: subscriptionData, refetch: refetchSubscription } = useQuery(
    GET_SUBSCRIPTION_INFO_STRIPE,
    { variables: { subAccountId, teamId }, fetchPolicy: 'network-only' },
  );

  const { data: taxData, refetch: refetchTax } = useQuery(GET_STRIPE_CUSTOMER_TAXABLE, {
    variables: { subAccountId, teamId },
    fetchPolicy: 'network-only',
  });

  const { data: upcomingData, refetch: refetchUpcoming } = useQuery(GET_UPCOMING_SUBSCRIPTION, {
    variables: { subAccountId, teamId },
    fetchPolicy: 'network-only',
    skip: isLimitedView,
  });

  // Outstanding-invoice signal (scio1 `res.invoices.length`). Intentionally NOT
  // skipped in limited view: the Update-Payment-Method / paywall flow is exactly
  // where the user has an unpaid invoice, and we must know it BEFORE the save so
  // handleCardSaved can show the scio1-parity auto-charge notice. `status:false`
  // = the account has unpaid invoices (same shape UnpaidInvoicesAlert reads).
  const { disablePaymentCheck } = useCompanyFeatures();
  const { data: unpaidData } = useQuery(GET_UPCOMING_AND_UNPAID_SUBSCRIPTION, {
    variables: { subAccountId, teamId },
    fetchPolicy: 'network-only',
    skip: disablePaymentCheck,
  });
  const hasUnpaidInvoices =
    (unpaidData as Record<string, { status?: boolean } | undefined> | undefined)
      ?.getUpcomingAndUnpaidSubscription?.status === false;

  // Fetch billing address from Stripe customer (matching scio1's getStripeCustomerInfo)
  const { data: customerInfoData } = useQuery(GET_STRIPE_CUSTOMER_INFO, {
    variables: { subAccountId, teamId },
    fetchPolicy: 'network-only',
    skip: isLimitedView,
  });

  const customerInfo = (customerInfoData as Record<string, unknown> | undefined)
    ?.getStripeCustomerInfo as
    | { billing_address?: Record<string, string>; phone?: string }
    | undefined;

  const [fetchSetupIntent, { loading: setupLoading }] = useLazyQuery(STRIPE_BUILD_ELEMENT, {
    fetchPolicy: 'network-only',
  });

  const [applyPromoCode, { loading: promoLoading }] = useMutation(UPDATE_PROMO_CODE_SUBSCRIPTION);
  const [registerPaymentMethod] = useMutation(REGISTER_PAYMENT_METHOD);
  // #1076 — scio1 paymentComponent.js:514 opens subscriptionPlanModal for trial
  // accounts after card save. scio3 equivalent: open the SubscriptionWizardDialog
  // via the global billing-dialog-store (BillingDialogs.tsx renders it).
  const openBillingDialog = useBillingDialogStore((s) => s.openDialog);
  // #1076 — Apollo client + auth store setter for manual company refresh after
  // card save. Backend registerPaymentMethod doesn't emit a pubsub COMPANY
  // UPDATE event (see backend billing.service.ts:144-228), so the Zustand
  // auth-store (which feeds existingCard via useBillingData) stays stale.
  // refetchQueries on the mutation is ineffective because AuthProvider uses
  // client.query() (one-off) not useQuery, so there's no observable to refetch.
  const apolloClient = useApolloClient();
  const setCompanyInStore = useAuthStore((s) => s.setCompany);

  const subscriptionInfo = (subscriptionData as Record<string, unknown> | undefined)
    ?.getSubscriptionInfoStripe as Record<string, unknown> | undefined;
  const taxIds = ((taxData as Record<string, unknown> | undefined)?.getStripeCustomerTaxable ??
    []) as Array<{
    id: string;
    code: string;
    value: string;
    type: string;
  }>;

  const upcomingInfo = (upcomingData as Record<string, unknown> | undefined)
    ?.getUpcomingSubscription as Record<string, unknown> | undefined;

  const discountInfo = useMemo(() => {
    const discount = upcomingInfo?.discount as Record<string, unknown> | undefined;
    if (!discount) return null;
    const coupon = discount.coupon as Record<string, unknown> | undefined;
    if (!coupon) return null;
    const percentOff = coupon.percent_off as number | undefined;
    const amountOff = coupon.amount_off as number | undefined;
    const duration = (coupon.duration as string) ?? '';
    let label = '';
    if (percentOff) label = `${percentOff}% ${t('off')}`;
    else if (amountOff) label = `$${(amountOff / 100).toFixed(2)} ${t('off')}`;
    if (duration) label += ` ${duration}`;
    return {
      name: (coupon.name as string) || ((discount.id as string) ?? ''),
      label,
    };
  }, [upcomingInfo, t]);

  const currency = (subscriptionInfo?.currency as string | undefined) ?? accountCurrency;

  // #1155 — scio1 parity (paymentBillingInformationComponent.html:139):
  // Tax ID is gated by billing-address country, not by Stripe currency.
  // Default for missing billing address is "US" in scio1, so require an
  // explicit non-US country to show the section.
  const billingCountry = customerInfo?.billing_address?.country?.toUpperCase();
  const showTaxIdSection = !!billingCountry && billingCountry !== 'US';

  const handleUpdateCard = useCallback(async () => {
    try {
      const result = await fetchSetupIntent({
        variables: { form: teamId ? 'TEAM' : 'BILLING_PAGE', currency, subAccountId, teamId },
      });
      const setupData = (result.data as Record<string, unknown> | undefined)?.stripeBuildElement as
        | { client_secret: string; paymentMethodOrder: string[] }
        | undefined;
      if (setupData?.client_secret) {
        setClientSecret(setupData.client_secret);
        setPaymentMethodOrder(setupData.paymentMethodOrder ?? []);
        setIsUpdatingCard(true);
      }
    } catch {
      toast.error(t('Failed to initialize payment form'));
    }
  }, [fetchSetupIntent, currency, subAccountId, teamId, t]);

  const handleCardSaved = useCallback(
    async (paymentMethodId: string) => {
      try {
        // #1076 — scio1 parity (paymentComponent.js afterSave, lines 506-590):
        //   if (myCompany.payment.isTrial)        → open Subscription Plan Modal (NO popup)
        //   else if (res.invoices.length > 0)     → show "going forward" warning popup
        //   else                                  → silent → go to thank-you page (NO popup)
        // Capture pre-save snapshot — useBillingData refetches below, so reading
        // these later would be stale. `hadUnpaidInvoices` must be read BEFORE the
        // charge, since a successful auto-charge clears the unpaid state.
        const wasTrial = isTrial;
        const hadUnpaidInvoices = hasUnpaidInvoices;
        // Register the payment method with backend (attach to Stripe customer, save to company.card)
        // chargeNow=true auto-charges any outstanding invoices right after the card is saved
        // (matching scio1). This handler is the SYNCHRONOUS card path (onSuccess of the card
        // element) — redirect PMs (iDEAL/Bancontact/Sofort) never reach here (they redirect out
        // and settle asynchronously via PaymentIntentPage), so charging synchronously is always
        // correct here. Do NOT tie this to `isLimitedView`: that flag means "show billing info
        // only / skip subscription queries", not "don't charge" — reusing it left the
        // Update-Payment-Method flow (isLimitedView=true) sending chargeNow=false, so a user with
        // an unpaid invoice could add a funded card yet never settle the invoice (stuck loop).
        const result = await registerPaymentMethod({
          variables: { paymentMethodId, chargeNow: true, subAccountId, teamId },
          // R-PAY-UNPAID-REFETCH — the synchronous AutoCharge (chargeNow) settles any outstanding invoice
          // inside this mutation, so refetch the mounted unpaid-invoice queries here to clear the display
          // without a manual page refresh: 'GetInvoicesUnpaid' (the UnpaidInvoicesSection list) +
          // 'GetUpcomingAndUnpaidSubscription' (page-level hasUnpaidInvoices). Contrast #1816's 3DS async
          // path, which defers its refetch to wizard-close because the charge settles after the mutation.
          refetchQueries: (teamId
            ? ['GetTeamBillingInfo']
            : subAccountId
              ? ['GetSubAccountInfo']
              : // Include 'Me' so the paywall (isAccountInactive) lifts after payment in limited/paywall flows
                ['GetCompanyInfo', 'Me']
          ).concat(['GetInvoicesUnpaid', 'GetUpcomingAndUnpaidSubscription']),
        });
        const response = (result.data as Record<string, unknown> | undefined)
          ?.registerPaymentMethod as { status?: boolean; message?: string } | undefined;
        if (response?.status === false) {
          toast.error(response.message ?? t('Failed to register payment method'));
          return;
        }
        setIsUpdatingCard(false);
        setClientSecret(null);
        refetchSubscription();
        // Manually refresh company in auth store — see comment on apolloClient
        // declaration above. Without this, the readonly view shows the OLD
        // card (e.g., card 1111 instead of just-saved iDEAL/SEPA).
        try {
          const companyResult = await apolloClient.query({
            query: GET_COMPANY_INFO,
            fetchPolicy: 'network-only',
          });
          const fresh = (companyResult.data as Record<string, unknown> | undefined)
            ?.getCompanyInfo as Parameters<typeof setCompanyInStore>[0] | undefined;
          if (fresh) setCompanyInStore(fresh);
        } catch {
          // Non-fatal — UI may show stale card briefly until next page refresh
        }
        if (wasTrial && !isLimitedView) {
          // Trial: scio1 opens subscriptionPlanModal here (paymentComponent.js:514).
          // scio3 equivalent → open SubscriptionWizardDialog so the user can pick a
          // plan now that they have a valid payment method. Suppressed in
          // limited/paywall view because the paywall surface drives its own flow
          // via onPaymentSaved.
          openBillingDialog('subscriptionWizard');
          onPaymentSaved?.();
        } else if (!wasTrial && hadUnpaidInvoices) {
          // Non-trial + had outstanding invoices → the auto-charge (chargeNow=true)
          // just ran against them. scio1 parity (paymentComponent.js afterSave, the
          // `res.invoices.length` branch): show the "going forward" notice popup.
          // scio3 previously documented this branch but never rendered it (#1076),
          // so an auto-charge gave the user no popup at all.
          setShowAutoChargeNotice(true);
        } else if (!wasTrial) {
          // Non-trial, no outstanding invoice → scio1-parity "Thank You!" popup
          // instead of the misleading scio3 SweetAlert (#1115). onPaymentSaved
          // fires when the user dismisses the dialog (see PaymentThankYouDialog).
          setShowThankYou(true);
        } else {
          // Trial + limited/paywall — paywall drives its own flow.
          onPaymentSaved?.();
        }
      } catch {
        toast.error(t('An error occurred while processing your card.'));
      }
    },
    [
      t,
      refetchSubscription,
      registerPaymentMethod,
      subAccountId,
      teamId,
      isLimitedView,
      onPaymentSaved,
      isTrial,
      hasUnpaidInvoices,
      openBillingDialog,
      apolloClient,
      setCompanyInStore,
    ],
  );

  const handleSaveEmail = useCallback(async () => {
    const trimmed = emailValue.trim().toLowerCase();
    if (!trimmed) {
      toast.error(t('Email is required'));
      return;
    }
    if (!isValidEmail(trimmed)) {
      toast.error(t('Please enter a valid email address'));
      return;
    }
    try {
      await updateStripeEmail({ variables: { email: trimmed, subAccountId, teamId } });
      toast(t('Your billing email has been saved!'));
      setIsEditingEmail(false);
    } catch {
      toast.error(t('Failed to update billing email'));
    }
  }, [emailValue, updateStripeEmail, subAccountId, teamId, t]);

  const handleCancelEmail = useCallback(() => {
    setEmailValue(billingEmail ?? defaultEmail);
    setIsEditingEmail(false);
  }, [billingEmail, defaultEmail]);

  const handleApplyPromo = useCallback(
    async (code: string): Promise<boolean> => {
      try {
        const result = await applyPromoCode({
          variables: { promoCode: code, subAccountId, teamId },
        });
        // scio1: returns null on success, error message string on failure
        const res = (result.data as Record<string, unknown> | undefined)
          ?.updatePromoCodeSubscription as string | null;
        if (res) {
          // res is the error message
          toast.error(t(res));
          return false;
        }
        refetchUpcoming();
        return true;
      } catch {
        toast.error(t('Failed to apply promo code'));
        return false;
      }
    },
    [applyPromoCode, subAccountId, teamId, t, refetchUpcoming],
  );

  // Card display — from company.card (loaded via GET_COMPANY_INFO at login)
  // paymentMethodId: 'card' for cards, 'sepa_debit'/'ideal'/etc. for non-card
  const hasCard = !!existingCard;
  const isNonCardPayment = existingCard?.paymentMethodId && existingCard.paymentMethodId !== 'card';

  // --- Guards (after all hooks) ---
  const isStandalone = !subAccountId && !teamId;
  if (isStandalone && !companyLoaded) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }
  if (isStandalone && !isRootAdmin) {
    return (
      <div className="space-y-6 p-6" data-testid="payment-method-page">
        <p className="text-sm text-muted-foreground">{t('You do not have access to this page')}</p>
      </div>
    );
  }
  if ((subAccountId || teamId) && loadingSubAccount) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4 p-6" data-testid="payment-method-page">
      {/* Warning banner for ?f param */}
      {hasFParam && (
        <Alert variant="destructive" data-testid="payment-warning-banner">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            {t(
              'Your subscription has been updated. Please update your payment method and contact support if you have any questions.',
            )}
          </AlertDescription>
        </Alert>
      )}

      {/* Auto-charge confirmation banner — shown for sub-accounts and updatePaymentMethod flow (matching scio1) */}
      {(!!subAccountId || isLimitedView) && (
        <Alert variant="default" className="border-warning bg-warning/10 dark:bg-warning/20">
          <AlertTriangle className="h-4 w-4 text-warning" />
          <AlertDescription>
            {t('The card will be automatically charged for next invoices going forward')}
          </AlertDescription>
        </Alert>
      )}

      {/* Unpaid invoices table — collapsible section matching scio1 */}
      <UnpaidInvoicesSection
        subAccountId={subAccountId}
        teamId={teamId}
        currency={accountCurrency}
      />

      {/* Section 1: Billing Email */}
      {!isLimitedView && (
        <CollapsibleSection
          title={t('Billing Email')}
          open={emailOpen}
          onOpenChange={setEmailOpen}
          data-testid="billing-email-section"
        >
          {isEditingEmail ? (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label htmlFor="billing-email" className="text-sm text-muted-foreground">
                  {t('Email')}
                </Label>
                <Input
                  id="billing-email"
                  data-testid="payment-billing-email-input"
                  type="email"
                  value={emailValue}
                  onChange={(e) => setEmailValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (isEnterKey(e)) {
                      e.preventDefault();
                      handleSaveEmail();
                    }
                  }}
                  placeholder={t('Billing Email')}
                />
              </div>
              <div className="flex justify-center gap-2">
                <Button
                  onClick={handleSaveEmail}
                  disabled={emailSaving}
                  className="cursor-pointer"
                  data-testid="payment-billing-email-save-btn"
                >
                  {emailSaving ? t('Saving...') : t('Save')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={handleCancelEmail}
                  className="cursor-pointer"
                  data-testid="payment-billing-email-cancel-btn"
                >
                  {t('Cancel')}
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="space-y-1">
                <p className="text-sm text-muted-foreground">{t('Email')}</p>
                <p className="text-sm" data-testid="payment-billing-email-display">
                  {billingEmail ?? defaultEmail ?? t('Not set')}
                </p>
              </div>
              <div className="flex justify-center">
                <Button
                  onClick={() => {
                    setEmailValue(billingEmail ?? defaultEmail);
                    setIsEditingEmail(true);
                  }}
                  className="cursor-pointer"
                  data-testid="payment-billing-email-update-btn"
                >
                  {t('Update Email')}
                </Button>
              </div>
            </div>
          )}
        </CollapsibleSection>
      )}

      {/* Section 2: Billing Information */}
      <CollapsibleSection
        title={t('Billing Information')}
        open={billingInfoOpen}
        onOpenChange={setBillingInfoOpen}
        data-testid="billing-info-section"
      >
        {/* View mode — card on file (from company.card via auth store) */}
        {!isUpdatingCard && (
          <div className="space-y-4">
            {/* Country + Zip/Postal Code (scio1 parity, #1146) */}
            {(existingCard?.address_country || existingCard?.address_zip) && (
              <div className="grid grid-cols-2 gap-4">
                <div className="border-b border-muted pb-2">
                  <p className="text-sm">
                    {existingCard?.address_country
                      ? COUNTRIES.find((c) => c.code === existingCard.address_country)?.name ??
                        existingCard.address_country
                      : '—'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{t('Zip / Postal Code')}</p>
                  <div className="border-b border-muted pb-2">
                    <p className="text-sm">{existingCard?.address_zip || '—'}</p>
                  </div>
                </div>
              </div>
            )}

            {hasCard && !isNonCardPayment ? (
              <>
                {/* Card number */}
                <div className="space-y-1">
                  <p className="text-xs text-muted-foreground">{t('Card number')}</p>
                  <div className="flex items-center justify-between border-b border-muted pb-2">
                    <p className="text-sm tracking-wider">**** **** **** {existingCard.last4}</p>
                    <CreditCard className="h-5 w-5 text-muted-foreground" />
                  </div>
                </div>
                {/* Expiration + CVV */}
                {existingCard.exp_month != null && existingCard.exp_year != null && (
                  <div className="grid grid-cols-[3fr_1fr] gap-4">
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">{t('Expiration')}</p>
                      <div className="border-b border-muted pb-2">
                        <p className="text-sm">
                          {existingCard.exp_month} / {existingCard.exp_year}
                        </p>
                      </div>
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs text-muted-foreground">{t('CVV')}</p>
                      <div className="border-b border-muted pb-2">
                        <p className="text-sm">****</p>
                      </div>
                    </div>
                  </div>
                )}
              </>
            ) : isNonCardPayment ? (
              <div className="space-y-1">
                <p className="text-xs text-muted-foreground">{t('Payment Method')}</p>
                <div className="border-b border-muted pb-2">
                  <p className="text-sm">
                    {t(
                      formatPaymentMethodLabel(
                        existingCard?.paymentMethodId || existingCard?.paymentMethod,
                      ),
                    )}
                  </p>
                </div>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">
                {t('No payment method on file')}
              </p>
            )}

            <div className="flex justify-center">
              <Button
                onClick={handleUpdateCard}
                disabled={setupLoading}
                className="cursor-pointer"
                data-testid="payment-billing-info-update-btn"
              >
                {setupLoading ? t('Loading...') : t('Update Payment Method')}
              </Button>
            </div>
          </div>
        )}

        {/* Edit mode — Stripe form */}
        {isUpdatingCard && clientSecret && (
          <div className="space-y-4" data-testid="payment-billing-info-edit-mode">
            <StripeCardForm
              clientSecret={clientSecret}
              paymentMethodOrder={paymentMethodOrder}
              hideEmail
              submitText={t('Save')}
              onSuccess={handleCardSaved}
              onError={(msg) => toast.error(msg)}
              // #1127: non-USD currencies expose iDEAL/Wero/SEPA/Bancontact tabs which need
              // an explicit billing address (Country + Address) for Stripe automatic tax.
              // StripeCardForm hides AddressElement on the Card tab, so USD users are unaffected.
              showBillingAddress={currency !== 'usd'}
            />
            <div className="flex justify-center">
              <Button
                variant="ghost"
                onClick={() => {
                  setIsUpdatingCard(false);
                  setClientSecret(null);
                }}
                className="cursor-pointer"
                data-testid="payment-billing-info-cancel-btn"
              >
                {t('Cancel')}
              </Button>
            </div>
          </div>
        )}
      </CollapsibleSection>

      {/* Section 3: Billing Address */}
      {!isLimitedView && (
        <CollapsibleSection
          title={t('Billing Address')}
          open={addressOpen}
          onOpenChange={setAddressOpen}
          data-testid="billing-address-section"
        >
          <BillingAddressForm
            initialValues={{
              line1: customerInfo?.billing_address?.line1 ?? '',
              line2: customerInfo?.billing_address?.line2 ?? '',
              country:
                customerInfo?.billing_address?.country ?? existingCard?.address_country ?? '',
              state: customerInfo?.billing_address?.state ?? existingCard?.address_state ?? '',
              postal_code:
                customerInfo?.billing_address?.postal_code ?? existingCard?.address_zip ?? '',
              city: customerInfo?.billing_address?.city ?? existingCard?.address_city ?? '',
              phone: customerInfo?.phone ?? '',
            }}
            subAccountId={subAccountId}
            teamId={teamId}
          />
        </CollapsibleSection>
      )}

      {/* Section 4: Tax ID — hidden for US-billed accounts per scio1 parity (#1155).
          scio1 (paymentBillingInformationComponent.html:139) gates this on
          billing-address country, not currency. Stripe auto-computes US sales
          tax, so US customers don't need to self-register a tax ID
          (VAT/GST/ABN etc. are for non-US billing). */}
      {!isLimitedView && showTaxIdSection && (
        <CollapsibleSection
          title={t('Tax ID')}
          open={taxOpen}
          onOpenChange={setTaxOpen}
          data-testid="tax-id-section"
        >
          <TaxIdSection
            taxIds={taxIds}
            subAccountId={subAccountId}
            teamId={teamId}
            onChanged={() => refetchTax()}
          />
        </CollapsibleSection>
      )}

      {/* Section 5: Discount */}
      {!isLimitedView && (
        <CollapsibleSection
          title={t('Discount')}
          open={discountOpen}
          onOpenChange={setDiscountOpen}
          data-testid="discount-section"
        >
          <PromoCodeInput
            onApply={handleApplyPromo}
            appliedCode={discountInfo?.name ?? null}
            discountLabel={discountInfo?.label ?? null}
            isLoading={promoLoading}
          />
        </CollapsibleSection>
      )}

      <PaymentThankYouDialog
        open={showThankYou}
        onContinue={() => {
          setShowThankYou(false);
          // Fire callback AFTER the user dismisses the popup so the paywall
          // dialog (if any) reloads the page only after acknowledgement.
          onPaymentSaved?.();
        }}
      />

      <AutoChargeNoticeDialog
        open={showAutoChargeNotice}
        onContinue={() => {
          setShowAutoChargeNotice(false);
          // Same as ThankYou: defer the callback until the user acknowledges,
          // so a paywall reload happens only after the notice is dismissed.
          onPaymentSaved?.();
        }}
      />
    </div>
  );
}
